import { Spacing } from '@persona-id/design-tokens';
import { ReactElement, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import GraphCollapsedGroupHoverCard from 'dashboard/components/GraphCollapsedGroupHoverCard';
import GraphNodeHoverCard from 'dashboard/components/GraphHoverCard/GraphNodeHoverCard';
import { useHoverCardState } from 'dashboard/components/GraphModule/hooks/useHoverCardState';
import {
  CollapsedGroup,
  CollapsedPairwiseNode,
  DatumTypeToken,
  Node,
  NodeRenderConfig,
} from 'dashboard/models/graph';
import { HydratedNode } from 'dashboard/models/graph-hydration';
import { Z_INDICES } from 'dashboard/styles/variables';
import { Elevation } from 'design-system/variables/effect';
import { BASE } from 'design-system/variables/spacing';

export type PureGraphHoverCardProps = {
  x: number;
  y: number;
  node: Node;
  hydratedNode: HydratedNode;
  collapsedGroup: CollapsedGroup;
  collapsedPairwiseNode: CollapsedPairwiseNode;
  hydratedCollapsedNodes: HydratedNode[];
} & GraphHoverCardProps;

const Container = styled('div')<{ x: number; y: number; mouseIn: boolean }>`
  position: absolute;
  z-index: ${Z_INDICES.modal};
  left: ${({ x }) => x}px;
  top: ${({ y }) => y}px;
  box-shadow: ${Elevation.High};
  border-radius: ${Spacing[2]};
  background-color: white;

  ${({ mouseIn }) => (mouseIn ? '' : 'user-select: none;')}
`;

export const PureGraphHoverCard = ({
  x,
  y,
  node,
  hydratedNode,
  collapsedGroup,
  collapsedPairwiseNode,
  hydratedCollapsedNodes,
  canvasHeight,
  canvasWidth,
  getNodeRenderConfig,
  actions,
}: PureGraphHoverCardProps) => {
  const [mouseIn, setMouseIn] = useState(false);
  const handleMouseOver = setMouseIn.bind(null, true);
  const handleMouseOut = setMouseIn.bind(null, false);

  // get size of the context menu container
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerDimensions, setCanvasDimensions] = useState({ width: 0, height: 0 });
  // once context menu changes size, get its height/width so we can move the menu if necessary
  useEffect(() => {
    const handleResize = () => {
      const newWidth = containerRef.current?.offsetWidth || containerDimensions.width;
      const newHeight = containerRef.current?.offsetHeight || containerDimensions.height;
      setCanvasDimensions({ width: newWidth, height: newHeight });
    };

    const current = containerRef.current;
    if (current) {
      handleResize();
      if (node?.hoverCards) {
        node.hoverCards.add(current);
      } else if (collapsedGroup?.hoverCards) {
        collapsedGroup.hoverCards.add(current);
      } else if (collapsedPairwiseNode?.hoverCards) {
        collapsedPairwiseNode.hoverCards.add(current);
      }
    }

    return () => {
      if (node?.hoverCards) {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        node.hoverCards.delete(current);
      } else if (collapsedGroup?.hoverCards) {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        collapsedGroup.hoverCards.delete(current);
      } else if (collapsedPairwiseNode?.hoverCards) {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        collapsedPairwiseNode.hoverCards.delete(current);
      }
    };
  }, [containerRef.current, node, collapsedGroup, collapsedPairwiseNode]);

  const getFinalY = () => {
    // if context menu leaves the screen, try moving it up so that it is in view
    const amountHidden = y + containerDimensions.height + BASE - canvasHeight;
    if (amountHidden > 0) {
      return y - amountHidden;
    }
    return y;
  };

  const getFinalX = () => {
    // if context menu leaves the screen, try moving it to the other side so that it is in view
    const amountHidden = x + containerDimensions.width + BASE - canvasWidth;
    // TODO (bjon): node size depends on zoom, but its kinda hard to get that value, hard code for now
    const nodeSize = 40;
    const otherSide = x - containerDimensions.width - nodeSize - BASE * 2;
    if (amountHidden > 0 && otherSide > 0) {
      return otherSide;
    }
    return x;
  };

  const isHovered = node != null || collapsedGroup != null || collapsedPairwiseNode != null;

  return x != null && y != null && isHovered ? (
    <Container
      ref={containerRef}
      data-graph-target='hover-card'
      x={getFinalX()}
      y={getFinalY()}
      mouseIn={mouseIn}
      onMouseOver={handleMouseOver}
      onMouseOut={handleMouseOut}
    >
      {node || collapsedPairwiseNode ? (
        <GraphNodeHoverCard
          node={node}
          collapsedPairwiseNode={collapsedPairwiseNode}
          hydratedNodes={hydratedNode != null ? [hydratedNode] : hydratedCollapsedNodes}
          getNodeRenderConfig={getNodeRenderConfig}
          actions={actions}
        />
      ) : collapsedGroup ? (
        <GraphCollapsedGroupHoverCard collapsedGroup={collapsedGroup} />
      ) : null}
    </Container>
  ) : null;
};

interface GraphHoverCardProps {
  canvasHeight: number;
  canvasWidth: number;
  getNodeRenderConfig: (token: DatumTypeToken) => NodeRenderConfig;
  actions?: (node: Node) => ReactElement[];
  xTransformer?: (v: number) => number;
  yTransformer?: (v: number) => number;
}

const GraphHoverCard = (props: GraphHoverCardProps) => {
  const {
    hoverCardX: x,
    hoverCardY: y,
    node,
    hydratedNode,
    collapsedGroup,
    collapsedPairwiseNode,
    hydratedCollapsedNodes,
  } = useHoverCardState({
    xTransformer: props.xTransformer,
    yTransformer: props.yTransformer,
  });
  return (
    <PureGraphHoverCard
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      x={x}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      y={y}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      node={node}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      hydratedNode={hydratedNode}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      collapsedGroup={collapsedGroup}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      collapsedPairwiseNode={collapsedPairwiseNode}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      hydratedCollapsedNodes={hydratedCollapsedNodes}
      {...props}
    />
  );
};

export default GraphHoverCard;
